/**
 * 知识库列表路由页：`/knowledge-bases/list`（顶栏 + 分类侧栏 + 右侧表格）。
 */
import { toast } from '@heroui/react';
import { useCallback, useState, type Key } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  buildInitialCanvasContent,
  DEFAULT_SLIDE_SIZE,
} from '@/components/canvas/document/buildInitialCanvasContent';
import { trpc } from '@/lib/trpc';
import { KnowledgeBaseHeader, type KnowledgeBasesScope } from '../components/KnowledgeBaseHeader';
import { KNOWLEDGE_BASE_SIDEBAR_ALL_ID } from '../components/constants';
import { KbCategorySidebar } from './components/KbCategorySidebar';
import { KnowledgeBasesDataList, type KnowledgeBaseRow } from './components/KbList';

export default function KnowledgeBasesListPage() {
  const navigate = useNavigate();
  const createDocMutation = trpc.knowledgeBases.create.useMutation();
  const createSlidesMutation = trpc.knowledgeBases.create.useMutation();
  const [scope, setScope] = useState<KnowledgeBasesScope>('my');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(KNOWLEDGE_BASE_SIDEBAR_ALL_ID);
  const [selectedCategoryName, setSelectedCategoryName] = useState<string | null>('全部');

  const handleScopeSegmentChange = useCallback((key: Key) => {
    const k = String(key) as KnowledgeBasesScope;
    if (k === 'my' || k === 'shared') {
      setScope(k);
      setSelectedCategoryId(KNOWLEDGE_BASE_SIDEBAR_ALL_ID);
      setSelectedCategoryName('全部');
    }
  }, []);

  const createNewDocAndNavigate = useCallback(async () => {
    try {
      const data = await createDocMutation.mutateAsync({
        title: '未命名',
        fileFormat: 'doc',
        content: {},
      });
      navigate(`/knowledge-bases/doc/${data.id}`);
    } catch {
      toast.danger('创建文档失败');
    }
  }, [createDocMutation, navigate]);

  const openNewSlidesCanvas = useCallback(async () => {
    try {
      const data = await createSlidesMutation.mutateAsync({
        title: '未命名',
        fileFormat: 'slides',
        content: buildInitialCanvasContent(DEFAULT_SLIDE_SIZE),
      });
      navigate(`/knowledge-bases/slides/${data.id}`);
    } catch {
      toast.danger('创建幻灯片失败');
    }
  }, [createSlidesMutation, navigate]);

  const handleOpenSlidesFromRow = useCallback(
    (row: KnowledgeBaseRow) => {
      navigate(`/knowledge-bases/slides/${row.id}`);
    },
    [navigate],
  );

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <KnowledgeBaseHeader
        scope={scope}
        onScopeChange={handleScopeSegmentChange}
        onNewDoc={() => void createNewDocAndNavigate()}
        onNewSlides={openNewSlidesCanvas}
      />
      <div className="flex min-h-0 min-w-0 flex-1">
        <KbCategorySidebar
          scope={scope}
          selectedCategoryId={selectedCategoryId}
          onCategorySelect={(id, name) => {
            setSelectedCategoryId(id);
            setSelectedCategoryName(name ?? null);
          }}
        />
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <KnowledgeBasesDataList
            scope={scope}
            selectedCategoryId={selectedCategoryId}
            selectedCategoryName={selectedCategoryName}
            onOpenSlides={handleOpenSlidesFromRow}
          />
        </div>
      </div>
    </div>
  );
}
